import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useLocale } from "@/i18n/useLocale";
import type { Locale } from "@/i18n/locales";
import { formatNumber } from "@/lib/format";

interface CountUpProps {
  to: number;
  duration?: number;
  /**
   * Receives the active locale as its second argument, so a caller can pass a
   * locale-aware formatter (`fmtUSD`, `formatPercent`) directly without closing
   * over the locale at every call site.
   */
  format?: (value: number, locale: Locale) => string;
  className?: string;
  style?: CSSProperties;
  /**
   * Optional external start trigger.
   *
   * - undefined (default): existing behaviour — CountUp registers
   *   its own IntersectionObserver and starts when the element
   *   enters view.
   * - true: skip the IO; run the rAF tick from 0 → to immediately.
   *   When the prop flips false → true again, the count restarts
   *   from 0.
   * - false: skip the IO; hold at 0.
   *
   * Useful when an outer component (e.g. <StickyShowcase>) needs
   * to defer / re-trigger the count based on its own activation
   * event rather than the CountUp element's viewport entry.
   */
  started?: boolean;
}

export default function CountUp({
  to,
  duration = 1400,
  format,
  className,
  style,
  started: externalStarted,
}: CountUpProps): JSX.Element {
  const locale = useLocale();
  // Defaults to the locale-aware formatter rather than a bare toLocaleString(),
  // so a CountUp that specifies no format still follows the SITE's locale.
  const fmt = (v: number): string => (format ? format(v, locale) : formatNumber(v, locale));
  const ref = useRef<HTMLSpanElement>(null);
  const [val, setVal] = useState(0);
  const [internalStarted, setInternalStarted] = useState(false);

  useEffect(() => {
    if (externalStarted !== undefined) return;
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInternalStarted(true);
            obs.disconnect();
          }
        }
      },
      { threshold: 0.4 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [externalStarted]);

  const effectiveStarted =
    externalStarted === undefined ? internalStarted : externalStarted;

  useEffect(() => {
    if (!effectiveStarted) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setVal(to);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / duration);
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      setVal(to * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [effectiveStarted, to, duration]);

  return (
    <span ref={ref} className={className} style={style}>
      {fmt(val)}
    </span>
  );
}
